import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import { formatMoney } from '@/utils/format';
import { isScheduleValid, type BulkTotals } from '@/utils/bulkPayments';
import type { BulkChannel, BulkRecipient, BulkSchedule } from '@/types/portal';
import PoolSweepPanel from './PoolSweepPanel';
import { SourceWalletCard, TransactionSummaryPanel } from './ReviewSidePanels';
import SchedulePaymentPanel from './SchedulePaymentPanel';

interface PoolInfo {
  accountName: string;
  accountNumber: string;
  bankName: string;
}

interface ReviewStepProps {
  recipients: BulkRecipient[];
  currency: string;
  channel: BulkChannel;
  totals: BulkTotals;
  walletName: string;
  walletBalance: number;
  walletAccount: string;
  pool: PoolInfo;
  schedule: BulkSchedule;
  onSchedule: (schedule: BulkSchedule) => void;
  reviewConfirmed: boolean;
  onReviewConfirmed: (value: boolean) => void;
  onInitiate: () => void;
  onBack: () => void;
  canApprove: boolean;
  /** On production non-approvers can't send: payments aren't held for approval (BACKEND_GAPS #64). */
  sendBlocked?: boolean;
}

export default function ReviewStep({
  recipients,
  currency,
  channel,
  totals,
  walletName,
  walletBalance,
  walletAccount,
  pool,
  schedule,
  onSchedule,
  reviewConfirmed,
  onReviewConfirmed,
  onInitiate,
  onBack,
  canApprove,
  sendBlocked = false,
}: ReviewStepProps) {
  const netOf = (row: BulkRecipient) => row.amount - (row.advance?.amount ?? 0);
  const scheduleReady = isScheduleValid(schedule);

  return (
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div className="space-y-6">
        <section className="rounded-lg border border-background-200 bg-background-50">
          <header className="flex items-center justify-between gap-3 border-b border-background-200 px-5 py-4">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
                <i className="ri-list-check-2" />
              </span>
              <div>
                <h3 className="font-heading text-sm font-semibold text-foreground-950">
                  Transaction Preview
                </h3>
                <p className="mt-0.5 text-xs text-foreground-500">
                  Review transactions before processing
                </p>
              </div>
            </div>
            <Badge tone="primary" icon="ri-exchange-dollar-line">
              {recipients.length} transaction{recipients.length === 1 ? '' : 's'}
            </Badge>
          </header>

          <div className="overflow-x-auto">
            <table className="row-accent w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                  <th className="w-12 px-5 py-3">#</th>
                  <th className="px-3 py-3">Wallet ID</th>
                  <th className="px-3 py-3">Recipient Name</th>
                  <th className="px-3 py-3">Amount ({currency})</th>
                  <th className="px-3 py-3">Description</th>
                  <th className="px-5 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((row, index) => (
                  <tr
                    key={row.id}
                    className="border-b border-background-100 last:border-0"
                  >
                    <td className="px-5 py-3 text-sm text-foreground-500">{index + 1}</td>
                    <td className="px-3 py-3 text-sm text-foreground-700">{row.walletId}</td>
                    <td className="px-3 py-3">
                      <p className="text-sm font-medium text-foreground-950">{row.name}</p>
                      <p className="text-xs text-foreground-500">
                        {row.campaign ?? row.market}
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="text-sm font-medium text-foreground-950">
                        {formatMoney(netOf(row), currency)}
                      </p>
                      {row.advance ? (
                        <p className="mt-0.5 text-[11px] text-secondary-800">
                          {formatMoney(row.advance.amount, currency)} swept to pool
                        </p>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-sm text-foreground-700">{row.description}</td>
                    <td className="px-5 py-3 text-right">
                      {row.status === 'Valid' ? (
                        <Badge tone="accent" icon="ri-check-line">
                          Valid
                        </Badge>
                      ) : (
                        <Badge tone="primary" icon="ri-error-warning-line">
                          {row.issue ?? 'Blocked'}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-background-200 px-5 py-3 text-xs text-foreground-500">
            <span>
              Showing {recipients.length} transaction{recipients.length === 1 ? '' : 's'}
            </span>
            {totals.blockedCount > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-primary-700">
                <i className="ri-error-warning-line" />
                {totals.blockedCount} blocked line{totals.blockedCount === 1 ? '' : 's'} will be
                skipped
              </span>
            ) : null}
          </div>
        </section>

        <PoolSweepPanel
          rows={recipients}
          currency={currency}
          accountName={pool.accountName}
          accountNumber={pool.accountNumber}
          bankName={pool.bankName}
        />

        <SchedulePaymentPanel schedule={schedule} onChange={onSchedule} />

        <section className="rounded-lg border border-background-200 bg-background-50 px-5 py-4">
          <button
            type="button"
            role="checkbox"
            aria-checked={reviewConfirmed}
            onClick={() => onReviewConfirmed(!reviewConfirmed)}
            className="flex w-full cursor-pointer items-start gap-3 text-left"
          >
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[13px] transition-colors ${
                reviewConfirmed
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-transparent'
              }`}
            >
              <i className="ri-check-line" />
            </span>
            <span className="text-sm text-foreground-700">
              I have reviewed the transactions above and confirm they are correct
            </span>
          </button>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button
              icon="ri-send-plane-line"
              onClick={onInitiate}
              disabled={!reviewConfirmed || totals.validCount === 0 || !scheduleReady || sendBlocked}
              title={sendBlocked ? 'Only an approver (Admin or Finance) can send payments.' : undefined}
            >
              {sendBlocked ? 'Only an approver can send' : canApprove ? 'Initiate Transaction' : 'Submit for approval'}
            </Button>
            <Button variant="outline" icon="ri-arrow-left-line" onClick={onBack}>
              Upload Different File
            </Button>
          </div>
        </section>
      </div>

      <div className="space-y-6">
        <SourceWalletCard
          walletName={walletName}
          currency={currency}
          balance={walletBalance}
          accountNumber={walletAccount}
        />
        <TransactionSummaryPanel totals={totals} currency={currency} channel={channel} />
      </div>
    </div>
  );
}